import { Button, Notification, Spinner, toast } from 'components/ui'
import React, { useContext, useEffect, useState } from 'react'
import ParticleBackground from 'react-particle-backgrounds'
import {  particleBackgroundSettingsButton } from 'configs/particle-background.config';
import { BsStars } from 'react-icons/bs';
import {  TbCircleCheckFilled } from 'react-icons/tb';
import CardDialog from 'views/subscription/CardDialog';
import { api as Api } from 'api/http';
import { formatNumber } from 'utils/formatNumber';
import { HiRocketLaunch } from 'react-icons/hi2';
import { Trans } from 'react-i18next';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { SessionContext } from 'contexts/SessionContext';
import { ModalContext } from 'contexts/ModalContext';
import { AppSumoContext } from 'contexts/AppSumoContext';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';

export default function PlusModal() {
  
  const stripe = useStripe();
  const elements = useElements();

  const { updateUserData, user } = useContext(SessionContext);
  const { closeModal } = useContext(ModalContext);
  const { isSumoling } = useContext(AppSumoContext);
  const { isWhitelabel, whitelabel } = useWhitelabel();

  const [ cardOpened, setCardOpened ] = useState(false);
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(true);
  const subscriptionRecurring = user?.subscription?.items?.[0]?.price?.recurring || 'month';
  const isFreePlan = !!user?.subscription?.items?.some(i => i?.product?.name == `free`);
  const [ billingFrequency, setBillingFrequency ] = useState(subscriptionRecurring)
  const [ item, setItem ] = useState();
  const [ products, setProducts ] = useState([]);
  const [ subscriptionCreated, setSubscriptionCreated ] = useState(false);

  const onSubmit = async (event) => {
    event && event?.preventDefault();
    setIsSubmitting(true);

    const plusPriceId = item?.prices?.filter(e => e.recurring == billingFrequency)[0]?.id;

    // On a free plan, re-map every existing subscription item to the price matching the
    // chosen billing interval, so the whole subscription stays on a single interval
    // (Stripe requires it). The free plan costs $0, so switching its interval has no
    // billing impact. On a paid plan we keep items as-is and force the add-on to the
    // existing billing frequency instead (see billingFrequency lock below).
    let newSubscription = (user?.subscription?.items || []).map((subItem) => {
      if (!isFreePlan) {
        return {
          "id": subItem.price.id,
          "quantity": subItem.quantity
        }
      }
      const product = products?.find(p => p.id == subItem?.product?.id);
      const matchingPrice = product?.prices?.find(p => p.recurring == billingFrequency);
      return {
        "id": matchingPrice?.id || subItem.price.id,
        "quantity": subItem.quantity
      }
    });
    
    if (newSubscription?.filter(item => item.id == plusPriceId)?.length == 0) {
      newSubscription.push({
        "id": plusPriceId,
        "quantity": 1
      });
    }

    let _paymentMethod;
    if (!user?.pm_last_four || !user?.subscription?.id) {
      const {error, paymentMethod} = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });
      if (error) {
        setIsSubmitting(false);
        toast.push(<Notification title={error.message} type="warning" />, { placement: 'top-center' });
        return;
      }
      _paymentMethod = paymentMethod;
    }

    const data = {
      payment_method_id: _paymentMethod?.id || undefined,
      prices: newSubscription
    };

    Api.put(`/subscriptions`, {}, data).then((response) => {
      setSubscriptionCreated(true);
      updateUserData();
      toast.push(<Notification title="Your subscription has been successfully modified." type="success" />, { placement: 'top-center' });
    }).catch((err) => {
      toast.push(<Notification title={err?.message || 'An error occurred. Please, try again later'} type="danger" />, { placement: 'top-center' });
    }).finally(() => {
      setIsSubmitting(false);
    });
  }

  // On a paid plan the add-on must follow the existing subscription interval, so the
  // billing-frequency toggle is locked. On a free plan the user can switch freely.
  const handleSelectFrequency = (frequency) => {
    if (!isFreePlan) return;
    setBillingFrequency(frequency);
  }

  const handleOnClick = () => {
    setCardOpened(true);
  }

  const handleOnCloseCardDialog = () => {
    setCardOpened(false);
    if (subscriptionCreated) {
      closeModal();
    }
  }
  
  useEffect(() => {
    setIsLoading(true);
    Api.get(`/products?include=prices`).then(({ data: products }) => {
      setProducts(products);
      setItem(products.find(e => e.name == `followr-plus`));
    }).finally(() => {
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!isSumoling()) {
      closeModal();
    }
  }, [isSumoling()]);

  if (isWhitelabel) return null;

  return (<>
    {!cardOpened
      ? <div className='relative'>
        <div className='bg-gradient-primary rounded-2xl overflow-hidden relative max-h-[90svh] overflow-y-auto'>
          <ParticleBackground settings={particleBackgroundSettingsButton} className="absolute pointer-events-none" />
          <div className='w-full h-full flex flex-col gap-8 lg:gap-10 items-center justify-center p-4 lg:p-8 py-8 lg:py-12 relative z-[1]'>
            <h2 className='mega-title text-white flex items-center gap-2'>
              {isWhitelabel ? (
                <i className='font-bold text-white text-4xl'>{whitelabel?.name}</i>
              ) : (
                <img 
                  src="/img/logo/logo-dark-full.png" 
                  className='h-8'
                />
              )}
              <span className='plus'>Plus</span>
              <BsStars className='stars' />
            </h2>
            <h3 className='text-white text-center text-lg lg:text-2xl font-black'>
              Take your content creation to the next level
            </h3>
            <p className='text-tertiary-300 text-center text-sm -mt-4 lg:-mt-6 font-bold'>
              Upgrade your account and benefit all your companies with {whitelabel?.name} <span className='plus'>Plus</span>. Experience total enhancements with the latest and best AI models available. 
            </p>
            <div className='grid lg:grid-cols-2 gap-2'>
              <div className='flex items-center gap-1.5 px-4 relative'>
                <div className='relative'>
                  <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-secondary-500 rounded-full' />
                  <TbCircleCheckFilled 
                    className='text-3xl text-green-500 relative z-[1]'
                  />
                </div>
                <span className='text-white text-xs font-bold'>Enhanced content creation tools</span>
              </div>
              <div className='flex items-center gap-1.5 px-4 relative'>
                <div className='relative'>
                  <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-secondary-500 rounded-full' />
                  <TbCircleCheckFilled 
                    className='text-3xl text-green-500 relative z-[1]'
                  />
                </div>
                <span className='text-white text-xs font-bold'>Innovative idea generation support</span>
              </div>
              <div className='flex items-center gap-1.5 px-4 relative'>
                <div className='relative'>
                  <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-secondary-500 rounded-full' />
                  <TbCircleCheckFilled 
                    className='text-3xl text-green-500 relative z-[1]'
                  />
                </div>
                <span className='text-white text-xs font-bold'>Improved image quality rendering</span>
              </div>
              <div className='flex items-center gap-1.5 px-4 relative'>
                <div className='relative'>
                  <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-secondary-500 rounded-full' />
                  <TbCircleCheckFilled 
                    className='text-3xl text-green-500 relative z-[1]'
                  />
                </div>
                <span className='text-white text-xs font-bold'>Reliable virtual assistance</span>
              </div>
            </div>

            <div className='flex flex-col gap-0 items-center'>
              <p className='text-white font-semibold text-sm'>For only</p>
              <h1 className='mega-title !text-5xl group-[.is-active]:!text-white text-right text-white'>
                {isLoading
                  ? <Spinner size="48" />
                  : <>${formatNumber((item?.prices?.filter(e => e.recurring == billingFrequency)[0])?.amount / 100, 2)}</>
                }
              </h1>
              <div className='flex gap-2 mt-4'>
                <div className={`transition-all ${isFreePlan ? `cursor-pointer` : `cursor-not-allowed`} flex items-center justify-between text-white gap-2 ${billingFrequency == `month` && `p-1 px-2.5 bg-secondary-500 font-semibold !text-tertiary-900 rounded-full`}`} >
                  <span onClick={() => handleSelectFrequency(`month`)}>
                    <Trans i18nKey={`subscription.monthly`}>Monthly</Trans>
                  </span>
                </div>
                <div className={`transition-all ${isFreePlan ? `cursor-pointer` : `cursor-not-allowed`} flex items-center justify-between text-white gap-2 ${billingFrequency == `year` && `p-1 px-2.5 bg-secondary-500 font-semibold !text-tertiary-900 rounded-full`}`} >
                  <span className='flex items-center gap-2 w-full' onClick={() => handleSelectFrequency(`year`)}>
                    <span><Trans i18nKey={`subscription.annualy`}>Annualy</Trans></span> 
                    <Button variant="ai" className='-mr-1 font-bold rounded-lg !p-1 !px-2.5 inline-block !h-auto !text-xs flex gap-1.5 items-center bg-primary w-auto'>
                      <HiRocketLaunch />
                      <span>-35%</span>
                    </Button>
                  </span>
                </div>
              </div>
            </div>

            <div className='flex justify-center gap-2 mt-2'>
              <Button 
                variant="ai"
                icon={<BsStars />}
                onClick={handleOnClick}
              >
                Upgrade now
              </Button>
            </div>
          </div>
        </div>
        {/* <img src="/img/others/behind-wall-top.png" className='absolute top-0 translate-y-[-71.5%] left-[10%] w-[402w%] drop-shadow-xl' /> */}
      </div>
      : <CardDialog 
        isOnlyContent={false}
        cardDialogOpened={cardOpened}
        setCardDialogOpened={() => handleOnCloseCardDialog()}
        handleSubmitStripe={onSubmit}
        isSubmittingCard={isSubmitting}
        subscriptionCreated={subscriptionCreated}
        isPlus={true}
      />
    }
  </>)
}
